// src/restaurant/pages/RestaurantNotifications.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, updateDoc, doc, orderBy, limit } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import RestaurantSidebar from '../../components/RestaurantSidebar';
import RestaurantHeader from '../../components/RestaurantHeader';
import './RestaurantNotifications.css';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useTranslation } from 'react-i18next';

const RestaurantNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read', 'chat'
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/restaurant/login');
        return;
      }

      const restaurantId = localStorage.getItem('restaurantId');
      if (!restaurantId) {
        navigate('/restaurant/login');
        return;
      }

      await Promise.all([
        fetchNotifications(restaurantId),
        fetchChatMessages(restaurantId)
      ]);
    });

    return () => unsubscribe();
  }, [navigate]);

  const fetchChatMessages = async (restaurantId) => {
    try {
      // جلب كل المحادثات بين المطعم والسائقين
      const chatsRef = collection(db, 'chats');
      const chatsSnapshot = await getDocs(chatsRef);
      const driverChats = chatsSnapshot.docs.filter(doc =>
        doc.id.startsWith(`restaurant_driver_${restaurantId}_`)
      );

      let messagesData = [];
      for (const chat of driverChats) {
        const messagesRef = collection(db, `chats/${chat.id}/messages`);
        const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(1)); // آخر رسالة فقط
        const messagesSnapshot = await getDocs(q);
        messagesSnapshot.forEach(doc => {
          messagesData.push({
            id: doc.id,
            ...doc.data(),
            senderName: doc.data().senderName || doc.data().sender || '',
            formattedDate: doc.data().timestamp ? formatTimestamp(doc.data().timestamp) : 'غير متاح',
            type: 'chat',
            chatId: chat.id,
          });
        });
      }
      setChatMessages(messagesData);
    } catch (error) {
      console.error('خطأ في جلب رسائل الشات:', error);
    }
  };

  const fetchNotifications = async (restaurantId) => {
    try {
      setLoading(true);
      
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('recipientId', '==', restaurantId),
        where('recipientType', '==', 'restaurant'),
        orderBy('timestamp', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const notificationsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        formattedDate: doc.data().timestamp ? formatTimestamp(doc.data().timestamp) : 'غير متاح'
      }));
      
      setNotifications(notificationsData);
    } catch (error) {
      console.error('خطأ في جلب الإشعارات:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp || !timestamp.toDate) return 'غير متاح';
    
    const date = timestamp.toDate();
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.round(diffMs / 60000);
    
    if (diffMins < 1) return 'الآن';
    if (diffMins < 60) return `منذ ${diffMins} دقيقة`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `منذ ${diffHours} ساعة`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `منذ ${diffDays} يوم`;
    
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const markAsRead = async (notificationId) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), { isRead: true });
      
      setNotifications(notifications.map(notification => 
        notification.id === notificationId 
          ? { ...notification, isRead: true } 
          : notification
      ));
    } catch (error) {
      console.error('خطأ في تحديث حالة الإشعار:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(notification => !notification.isRead);
      
      if (unreadNotifications.length === 0) return;
      
      // تحديث كل الإشعارات غير المقروءة
      const updatePromises = unreadNotifications.map(notification => 
        updateDoc(doc(db, 'notifications', notification.id), { isRead: true })
      );
      
      await Promise.all(updatePromises);
      
      // تحديث الحالة المحلية
      setNotifications(notifications.map(notification => ({ ...notification, isRead: true })));
    } catch (error) {
      console.error('خطأ في تحديث حالة الإشعارات:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (notification.type === 'chat') {
      // توجيه إلى صفحة الشات مع معرف المرسل
      navigate(`/restaurant/chat/${notification.senderId}`);
    } else {
      // معالجة الإشعارات العادية
      markAsRead(notification.id);
    }
  };

  const filteredContent = [...notifications, ...chatMessages].filter(item => {
    if (filter === 'unread') return !item.isRead;
    if (filter === 'read') return item.isRead;
    if (filter === 'chat') return item.type === 'chat';
    return true;
  });

  if (loading) {
    return <LoadingSpinner text={t('loading')} />;
  }

  return (
    <div className="restaurant-notifications-page">
      <RestaurantSidebar />
      <div className="restaurant-notifications-content">
        <RestaurantHeader restaurantName={localStorage.getItem('restaurantName') || 'المطعم'} />
        
        <div className="notifications-container">
          <div className="notifications-header">
            <h1>{t('notifications')}</h1>
            <div className="notifications-actions">
              <button 
                className="mark-all-btn" 
                onClick={markAllAsRead}
                disabled={!notifications.some(notification => !notification.isRead)}
              >
                {t('mark_all_as_read')}
              </button>
            </div>
          </div>
          
          <div className="notifications-filters">
            <button 
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`} 
              onClick={() => setFilter('all')}
            >
              {t('all')}
            </button>
            <button 
              className={`filter-btn ${filter === 'unread' ? 'active' : ''}`} 
              onClick={() => setFilter('unread')}
            >
              {t('unread')}
            </button>
            <button 
              className={`filter-btn ${filter === 'read' ? 'active' : ''}`} 
              onClick={() => setFilter('read')}
            >
              {t('read')}
            </button>
            <button 
              className={`filter-btn ${filter === 'chat' ? 'active' : ''}`} 
              onClick={() => setFilter('chat')}
            >
              {t('chat_messages')}
            </button>
          </div>
          
          {filteredContent.length === 0 ? (
            <div className="no-notifications">
              <p>{t('no_notifications')}{filter !== 'all' ? ` ${t('in_this_category')}` : ''}</p>
            </div>
          ) : (
            <div className="notifications-list">
              {filteredContent.map(item => (
                <div 
                  key={item.id} 
                  className={`notification-item ${!item.isRead ? 'unread' : ''} ${item.type === 'chat' ? 'chat-message' : ''}`}
                  onClick={() => handleNotificationClick(item)}
                >
                  <div className="notification-content">
                    {item.type === 'chat' ? (
                      <>
                        <div className="chat-preview">
                          <span className="sender-name">{item.senderName}</span>
                          <p className="message-preview">{item.message}</p>
                        </div>
                        <p className="notification-time">{item.formattedDate}</p>
                      </>
                    ) : (
                      <>
                        <h3>{item.message}</h3>
                        <p className="notification-time">{item.formattedDate}</p>
                      </>
                    )}
                  </div>
                  {!item.isRead && item.type !== 'chat' && (
                    <button 
                      className="mark-read-btn" 
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(item.id);
                      }}
                    >
                      {t('mark_as_read')}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RestaurantNotifications;